import React, { useEffect, useMemo, useState } from "react";
import { adminApi, API } from "@/lib/adminApi";
import { AdminPageHeader, Card, EmptyState, Spinner, ErrorBanner, Btn, Tag } from "./_shared";
import { FileText, Download, RefreshCw, ArrowLeft, Sparkles } from "lucide-react";

const AI_MODES = [
  { id: "none", label: "Rule-based", hint: "Free · zero LLM calls" },
  { id: "light", label: "AI-light", hint: "1 Claude call · exec summary + themes" },
  { id: "rich", label: "AI-rich", hint: "2 Claude calls · adds demographic / category lens" },
];

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : "0");

const Bar = ({ pct, accent = "#FF9933" }) => (
  <div className="h-2 bg-[#1a1a1a] border border-[#2a2a2a]">
    <div
      className="h-full transition-all"
      style={{ width: `${Math.min(100, pct)}%`, background: accent }}
    />
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-xs uppercase font-extrabold tracking-[0.35em] mb-3" style={{ color: "#FF9933" }}>
    {children}
  </h3>
);

export const Reports = () => {
  const [polls, setPolls] = useState(null);
  const [pollsErr, setPollsErr] = useState(null);
  const [selected, setSelected] = useState(null); // slug
  const [aiMode, setAiMode] = useState("none");
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportErr, setReportErr] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/reports/polls")
      .then((r) => setPolls(r.data?.polls || []))
      .catch((e) => setPollsErr(e?.response?.data?.detail || "Could not load polls."));
  }, []);

  const fetchReport = (slug, mode) => {
    setLoadingReport(true);
    setReportErr(null);
    setReport(null);
    adminApi.get(`/admin/reports/${slug}`, { params: { ai_mode: mode } })
      .then((r) => setReport(r.data))
      .catch((e) => setReportErr(e?.response?.data?.detail || "Could not generate report."))
      .finally(() => setLoadingReport(false));
  };

  const handleSelect = (slug) => {
    setSelected(slug);
    setAiMode("none");
    fetchReport(slug, "none");
  };

  const handleModeChange = (m) => {
    setAiMode(m);
    if (selected) fetchReport(selected, m);
  };

  const handleDownloadPdf = async () => {
    if (!selected) return;
    setPdfDownloading(true);
    try {
      const resp = await adminApi.get(`/admin/reports/${selected}/pdf`, {
        params: { ai_mode: aiMode },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kerala-speaks-report-${selected}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setReportErr(e?.response?.data?.detail || "PDF download failed.");
    } finally {
      setPdfDownloading(false);
    }
  };

  if (!polls && !pollsErr) {
    return (
      <div className="space-y-6" data-testid="reports-loading">
        <AdminPageHeader eyebrow="Reports" title="Civic Insight Reports" sub="Generate clean, structured reports for any poll on Kerala Speaks." />
        <Spinner label="Loading polls…" />
      </div>
    );
  }

  if (pollsErr) {
    return (
      <div className="space-y-6">
        <AdminPageHeader eyebrow="Reports" title="Civic Insight Reports" />
        <ErrorBanner message={pollsErr} />
      </div>
    );
  }

  // List view
  if (!selected) {
    return (
      <div className="space-y-6" data-testid="reports-list-view">
        <AdminPageHeader
          eyebrow="Reports"
          title="Civic Insight Reports"
          sub="Pick a poll to generate a visual report. Rule-based reports are free; AI-light and AI-rich modes use Claude Sonnet and are billed against your Universal Key."
        />
        {polls.length === 0 ? (
          <EmptyState title="No polls yet." sub="Create a poll in the multi-poll engine first." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="reports-poll-grid">
            {polls.map((p) => (
              <button
                key={p.slug}
                onClick={() => handleSelect(p.slug)}
                data-testid={`reports-poll-card-${p.slug}`}
                className="text-left border-2 border-[#1f1f1f] hover:border-[#FF9933] p-5 transition-colors group"
                style={{ background: "#141414" }}
              >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Tag color={p.poll_type === "multiple_choice" ? "#FF9933" : "#9bc6ff"}>
                    {p.poll_type === "multiple_choice" ? "Multiple Choice" : "Open Conversation"}
                  </Tag>
                  <Tag color={p.status === "active" ? "#22c55e" : "#a3a3a3"}>{p.status}</Tag>
                  {p.category && <Tag color="#FDFBF7">{p.category}</Tag>}
                </div>
                <p className="font-display uppercase text-lg leading-tight text-[#FDFBF7] group-hover:text-[#FF9933] transition-colors">
                  {p.title || p.headline}
                </p>
                <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{p.headline}</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-3 flex items-center gap-2">
                  <FileText size={11} strokeWidth={3} /> Generate report →
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Detail view
  return (
    <div className="space-y-6" data-testid="reports-detail-view">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => { setSelected(null); setReport(null); setReportErr(null); }}
          className="flex items-center gap-2 text-[10px] uppercase font-extrabold tracking-[0.35em] text-neutral-500 hover:text-[#FF9933] transition-colors"
          data-testid="reports-back"
        >
          <ArrowLeft size={12} strokeWidth={3} /> All Reports
        </button>
        <div className="flex items-center gap-2 flex-wrap" data-testid="reports-mode-picker">
          {AI_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => handleModeChange(m.id)}
              data-testid={`reports-mode-${m.id}`}
              className="border-2 px-3 py-1.5 text-[10px] uppercase font-extrabold tracking-[0.25em] transition-colors"
              style={{
                background: aiMode === m.id ? "#FF9933" : "transparent",
                color: aiMode === m.id ? "#0A0A0A" : "#FDFBF7",
                borderColor: aiMode === m.id ? "#FF9933" : "#1f1f1f",
              }}
              title={m.hint}
            >
              {m.label}
            </button>
          ))}
          <Btn
            onClick={handleDownloadPdf}
            disabled={pdfDownloading || loadingReport || !report}
            data-testid="reports-download-pdf"
          >
            <Download size={12} strokeWidth={3} />
            {pdfDownloading ? "Preparing…" : "Download PDF"}
          </Btn>
          <Btn
            variant="ghost"
            onClick={() => fetchReport(selected, aiMode)}
            disabled={loadingReport}
            data-testid="reports-refresh"
          >
            <RefreshCw size={12} strokeWidth={3} />
            Refresh
          </Btn>
        </div>
      </div>

      {loadingReport && (
        <Spinner label={aiMode === "none" ? "Crunching numbers…" : "Asking Claude for the executive summary…"} />
      )}
      {reportErr && <ErrorBanner message={reportErr} />}
      {report && !loadingReport && <ReportView report={report} />}
    </div>
  );
};

const ReportView = ({ report }) => {
  const t = report.totals || {};
  const ai = report.ai || {};
  const isMC = report.poll_type === "multiple_choice";
  const generated = useMemo(
    () => (report.generated_at ? new Date(report.generated_at).toLocaleString() : ""),
    [report.generated_at],
  );

  return (
    <div className="space-y-6" data-testid="report-view">
      {/* Header */}
      <div className="border-2 border-[#1f1f1f] p-5 md:p-7" style={{ background: "#141414" }}>
        <p className="text-[10px] uppercase font-extrabold tracking-[0.4em]" style={{ color: "#FF9933" }}>
          ★ KERALA SPEAKS · CIVIC INSIGHT REPORT ★
        </p>
        <h2
          className="font-display uppercase leading-[0.95] tracking-tight mt-3 text-[#FDFBF7]"
          style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}
          data-testid="report-title"
        >
          {report.title || report.headline}
        </h2>
        <p className="text-sm text-neutral-300 mt-3 leading-relaxed max-w-3xl">{report.headline}</p>
        {report.subtext && <p className="text-xs text-neutral-500 mt-2 max-w-3xl">{report.subtext}</p>}
        <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-600 mt-4">
          Generated {generated} · Mode: {ai.mode || "none"} · Slug: {report.slug}
        </p>
      </div>

      {/* Key numbers */}
      <Card title="Key Numbers" testId="report-key-numbers">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {isMC ? (
            <>
              <Big label="Total votes" value={fmtNum(t.votes)} testId="kpi-votes" />
              <Big label="Days active" value={fmtNum(t.days_active)} testId="kpi-days" />
              <Big label="Comments" value={`${fmtNum(t.comments_total)} (${fmtNum(t.comments_approved)} ok)`} testId="kpi-comments" />
              <Big label="Profiles · Districts" value={`${fmtNum(t.profiles_collected)} · ${fmtNum(t.unique_districts)}`} testId="kpi-profiles" />
            </>
          ) : (
            <>
              <Big label="Total messages" value={fmtNum(t.messages)} testId="kpi-messages" />
              <Big label="Total upvotes" value={fmtNum(t.upvotes)} testId="kpi-upvotes" />
              <Big label="Days active" value={fmtNum(t.days_active)} testId="kpi-days" />
              <Big label="Categories" value={fmtNum(t.categories)} testId="kpi-categories" />
            </>
          )}
        </div>
      </Card>

      {/* Executive summary */}
      <Card
        title={ai.mode === "none" ? "Executive Summary (rule-based)" : `Executive Summary (Claude · ${ai.mode})`}
        testId="report-summary"
        action={
          ai.mode !== "none" && (
            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-1.5">
              <Sparkles size={11} strokeWidth={3} /> AI-generated
            </span>
          )
        }
      >
        <div className="prose prose-invert max-w-none" data-testid="report-exec-summary">
          {(ai.exec_summary || "(no summary)").split("\n\n").map((p, i) => (
            <p key={i} className="text-sm text-neutral-200 leading-relaxed mb-3 whitespace-pre-wrap">{p}</p>
          ))}
        </div>
        {ai.themes && ai.themes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#1f1f1f]">
            <SectionTitle>Key Themes</SectionTitle>
            <ul className="space-y-2" data-testid="report-themes">
              {ai.themes.map((th, i) => (
                <li key={i} className="text-sm text-neutral-200 flex items-start gap-2">
                  <span style={{ color: "#FF9933" }} className="font-extrabold">›</span>
                  <span>{th}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {ai.demographic_lens && (
          <div className="mt-4 pt-4 border-t border-[#1f1f1f]">
            <SectionTitle>{isMC ? "Demographic Lens" : "Category Lens"}</SectionTitle>
            <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid="report-demo-lens">
              {ai.demographic_lens}
            </p>
          </div>
        )}
        {ai.ai_error && (
          <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-500 mt-3">
            Note: AI call failed — rule-based summary shown instead.
          </p>
        )}
        {ai.mode === "none" && Array.isArray(ai.key_points) && (
          <div className="mt-4 pt-4 border-t border-[#1f1f1f]">
            <SectionTitle>Headline numbers</SectionTitle>
            <ul className="space-y-1.5" data-testid="report-key-points">
              {ai.key_points.map((pt, i) => (
                <li key={i} className="text-sm text-neutral-300 flex items-start gap-2">
                  <span style={{ color: "#FF9933" }}>•</span>
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* MC: Options + Demographics + Comments */}
      {isMC && (
        <>
          <Card title="Vote Distribution" testId="report-options">
            <div className="space-y-3" data-testid="report-option-list">
              {(report.options || []).map((o) => {
                const winning = report.winner?.id === o.id;
                return (
                  <div key={o.id} data-testid={`report-option-${o.id}`}>
                    <div className="flex items-center justify-between mb-1 gap-3">
                      <span className="text-sm text-neutral-200">
                        {winning && <span style={{ color: "#FF9933" }} className="mr-1">★</span>}
                        {o.label}
                      </span>
                      <span className="text-xs font-bold text-neutral-300 shrink-0">
                        {o.percentage.toFixed(1)}% · {fmtNum(o.votes)}
                      </span>
                    </div>
                    <Bar pct={o.percentage} accent={winning ? "#FF9933" : "#9bc6ff"} />
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            <DemographicCard title="By Age Group" rows={report.demographics?.by_age} testId="demo-age" />
            <DemographicCard title="By District" rows={report.demographics?.by_district} testId="demo-district" />
            <DemographicCard title="By Occupation" rows={report.demographics?.by_occupation} testId="demo-occupation" />
            <DemographicCard title="By Gender" rows={report.demographics?.by_gender} testId="demo-gender" />
          </div>

          {(report.comments_sample || []).length > 0 && (
            <Card title="Selected Citizen Voices" testId="report-comments">
              <div className="space-y-3" data-testid="report-comments-list">
                {report.comments_sample.map((c, i) => (
                  <div key={i} className="border-l-2 pl-3" style={{ borderColor: "#FF9933" }}>
                    <p className="text-sm text-neutral-200 italic">“{c.text}”</p>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">
                      {[c.district, c.age_group].filter(Boolean).join(" · ") || "—"} · {c.upvotes} upvotes
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Open: Categories + Top messages */}
      {!isMC && (
        <>
          <Card title="Category Breakdown" testId="report-categories">
            <div className="space-y-3" data-testid="report-category-list">
              {(report.by_category || []).map((c) => (
                <div key={c.category} data-testid={`report-cat-${c.category}`}>
                  <div className="flex items-center justify-between mb-1 gap-3">
                    <span className="text-sm text-neutral-200 capitalize">{String(c.category).replace(/-/g, " ")}</span>
                    <span className="text-xs font-bold text-neutral-300 shrink-0">
                      {c.percentage.toFixed(1)}% · {fmtNum(c.count)} msgs · {fmtNum(c.upvotes)} upvotes
                    </span>
                  </div>
                  <Bar pct={c.percentage} />
                </div>
              ))}
            </div>
          </Card>

          {(report.top_messages || []).length > 0 && (
            <Card title="Top Citizen Messages" testId="report-top-messages">
              <div className="space-y-3" data-testid="report-top-messages-list">
                {report.top_messages.map((m, i) => (
                  <div key={i} className="border-l-2 pl-3" style={{ borderColor: "#FF9933" }}>
                    <p className="text-sm text-neutral-200 italic">“{m.message}”</p>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1 capitalize">
                      {String(m.category || "—").replace(/-/g, " ")} · {m.upvotes} upvotes
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600">
        This report aggregates voluntary citizen input. Demographic figures reflect only voters who chose to share a profile.
      </p>
    </div>
  );
};

const Big = ({ label, value, testId }) => (
  <div className="border-2 border-[#1f1f1f] p-3" style={{ background: "#0F0F0F" }} data-testid={testId}>
    <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">{label}</p>
    <p className="font-display text-2xl md:text-3xl mt-1 leading-none text-[#FDFBF7]">{value}</p>
  </div>
);

const DemographicCard = ({ title, rows, testId }) => {
  if (!rows || rows.length === 0) {
    return (
      <Card title={title} testId={testId}>
        <p className="text-xs text-neutral-600 uppercase tracking-[0.3em]">No data yet</p>
      </Card>
    );
  }
  return (
    <Card title={title} testId={testId}>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-neutral-200">{r.key}</span>
            <span className="text-xs font-bold text-neutral-400 shrink-0">
              {r.percentage.toFixed(1)}% · {r.count}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default Reports;
